import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { BarChart3, RefreshCw } from "lucide-react";
import { useState } from "react";

import { useUsageStats } from "@/hooks/useUsageStats";

import { PageViewHeader } from "./PageViewHeader";
import { CircuitUsageStats } from "./stats/CircuitUsageStats";
import { formatInt } from "./stats/formatters";
import { StatsAdvanced, type StatsDetailSection } from "./stats/StatsAdvanced";
import { StatsOverview } from "./stats/StatsOverview";
import { StatsPerformance } from "./stats/StatsPerformance";

type StatsTab = "overview" | "circuits" | "performance" | StatsDetailSection;

const STATS_TABS: Array<{ id: StatsTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "circuits", label: "Circuits" },
  { id: "performance", label: "Performance" },
  { id: "tokens", label: "Tokens & steps" },
  { id: "tools", label: "Tools" },
  { id: "capabilities", label: "Capabilities" },
  { id: "cost", label: "Cost" },
];

export function UsageStatsView(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(14);
  const [mine, setMine] = useState(true);
  const [tab, setTab] = useState<StatsTab>("overview");
  const { data, isLoading, isFetching, refetch } = useUsageStats(days, mine);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="Stats"
        badge={
          data ? (
            <span className="bg-cyber-surface text-cyber-muted rounded-full px-1.5 py-0.5 text-[10px]">
              {formatInt(data.totals.responses)} responses
            </span>
          ) : undefined
        }
        actions={
          <>
            <div className="border-cyber-border flex items-center overflow-hidden rounded border">
              {[7, 14, 30].map((value) => (
                <button
                  key={value}
                  onClick={() => setDays(value)}
                  className={clsx(
                    "px-2 py-1 text-xs transition-colors",
                    days === value
                      ? "bg-cyber-cyan/10 text-cyber-cyan"
                      : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                  )}
                >
                  {value}d
                </button>
              ))}
            </div>

            <button
              onClick={() => setMine((current) => !current)}
              className={clsx(
                "rounded border px-2.5 py-1 text-xs transition-colors",
                mine
                  ? "border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan"
                  : "border-cyber-border text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              {mine ? "Mine" : "All users"}
            </button>

            <button
              onClick={() => {
                void refetch();
                void queryClient.invalidateQueries({ queryKey: ["performance"] });
              }}
              className="text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-cyan rounded p-1.5 transition-colors"
              title="Refresh stats"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            </button>
          </>
        }
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          <div
            role="tablist"
            aria-label="Stats sections"
            className="border-cyber-border flex flex-wrap gap-x-1 border-b"
          >
            {STATS_TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={clsx(
                  "border-b-2 px-3 py-2 text-xs transition-colors",
                  tab === id
                    ? "border-cyber-cyan text-cyber-cyan"
                    : "text-cyber-muted hover:text-cyber-text border-transparent",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "performance" ? (
            <StatsPerformance key={`${days}:${mine}`} days={days} mine={mine} />
          ) : isLoading && !data ? (
            <div className="text-cyber-muted py-8 text-center">
              <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
              Loading usage stats...
            </div>
          ) : !data || (data.totals.responses === 0 && data.capabilityUsage.length === 0) ? (
            <div className="py-14 text-center">
              <BarChart3 size={32} className="text-cyber-muted/30 mx-auto mb-3" />
              <p className="text-cyber-muted mb-1 text-sm">No usage data yet</p>
              <p className="text-cyber-muted/60 text-xs">
                Run some prompts or remote capabilities, then stats will appear here.
              </p>
            </div>
          ) : tab === "overview" ? (
            <StatsOverview data={data} />
          ) : tab === "circuits" ? (
            <CircuitUsageStats data={data} />
          ) : (
            <StatsAdvanced data={data} section={tab} />
          )}
        </div>
      </div>
    </div>
  );
}
